import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    Snackbar,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import HelpIcon from "@mui/icons-material/Help";
import { getServiceSettings, updateServiceSettings } from "../../../api/api";
import { getCurrentBranchId } from "../../../utils/auth";
import OnboardingTutorialOverlay from "../../../components/OnboardingTutorialOverlay";
import { CUSTOMER_FLOW_TEXT_DEFAULTS } from "../CustomerView/useCustomerFlowTexts";

// Preview screenshots
import previewNextButton from "../../../assests/TextEditorGuide/next_button.png";
import previewServiceSelectionText from "../../../assests/TextEditorGuide/service_selection_text.png";
import previewQueueNumberLabel from "../../../assests/TextEditorGuide/queue_number_label.png";
import previewBeingServedMessage from "../../../assests/TextEditorGuide/being_served_message.png";
import previewSelectedServicesLabel from "../../../assests/TextEditorGuide/selected_services_label.png";
import previewThankYouMessage from "../../../assests/TextEditorGuide/thank_you_message.png";
import previewThankYouDescription from "../../../assests/TextEditorGuide/thank_you_description.png";
import previewQrPageTitle from "../../../assests/TextEditorGuide/qr_page_title.png";

const TextEditor = () => {
    const branchId = getCurrentBranchId();
    const [serviceSelectionText, setServiceSelectionText] = useState("");
    const [thankYouMessage, setThankYouMessage] = useState("");
    const [thankYouDescription, setThankYouDescription] = useState("");
    const [beingServedMessage, setBeingServedMessage] = useState("");
    const [nextButtonText, setNextButtonText] = useState("");
    const [queueNumberLabel, setQueueNumberLabel] = useState("");
    const [selectedServicesLabel, setSelectedServicesLabel] = useState("");
    const [waitingTimeMessage, setWaitingTimeMessage] = useState("");
    const [serviceRecommendationText, setServiceRecommendationText] = useState("");
    const [qrPageTitle, setQrPageTitle] = useState("");
    const [thankYouServicesLabel, setThankYouServicesLabel] = useState("");
    const [qrScanText, setQrScanText] = useState("");
    const [smsPhoneText, setSmsPhoneText] = useState("");
    const [smsRegisterButtonText, setSmsRegisterButtonText] = useState("");
    const [smsConfirmationText, setSmsConfirmationText] = useState("");
    const [smsNewQueueButtonText, setSmsNewQueueButtonText] = useState("");
    const [savingQrScanText, setSavingQrScanText] = useState(false);
    const [savingSmsPhoneText, setSavingSmsPhoneText] = useState(false);
    const [savingSmsRegisterButtonText, setSavingSmsRegisterButtonText] = useState(false);
    const [savingSmsConfirmationText, setSavingSmsConfirmationText] = useState(false);
    const [savingSmsNewQueueButtonText, setSavingSmsNewQueueButtonText] = useState(false);
    const [savingQrPageTitle, setSavingQrPageTitle] = useState(false);
    const [savingThankYouServicesLabel, setSavingThankYouServicesLabel] = useState(false);
    const [savingServiceSelectionText, setSavingServiceSelectionText] = useState(false);
    const [savingThankYouMessage, setSavingThankYouMessage] = useState(false);
    const [savingThankYouDescription, setSavingThankYouDescription] = useState(false);
    const [savingBeingServedMessage, setSavingBeingServedMessage] = useState(false);
    const [savingNextButtonText, setSavingNextButtonText] = useState(false);
    const [savingQueueNumberLabel, setSavingQueueNumberLabel] = useState(false);
    const [savingSelectedServicesLabel, setSavingSelectedServicesLabel] = useState(false);
    const [savingWaitingTimeMessage, setSavingWaitingTimeMessage] = useState(false);
    const [savingServiceRecommendationText, setSavingServiceRecommendationText] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
    const [previewModal, setPreviewModal] = useState({ open: false, image: null, label: "" });
    const [zoomOrigin, setZoomOrigin] = useState("center center");
    const [isZoomed, setIsZoomed] = useState(false);
    const [runTutorial, setRunTutorial] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await getServiceSettings(branchId);
                if (response.success) {
                    setServiceSelectionText(response.data.service_selection_text || "");
                    setThankYouMessage(response.data.thank_you_message || "");
                    setThankYouDescription(response.data.thank_you_description || "");
                    setBeingServedMessage(response.data.being_served_message || "");
                    setNextButtonText(response.data.next_button_text || "");
                    setQueueNumberLabel(response.data.queue_number_label || "");
                    setSelectedServicesLabel(response.data.selected_services_label || "");
                    setWaitingTimeMessage(response.data.waiting_time_message || "");
                    setServiceRecommendationText(response.data.service_recommendation_text || "");
                    setQrPageTitle(response.data.qr_page_title || "");
                    setThankYouServicesLabel(response.data.thank_you_services_label || "");
                    setQrScanText(response.data.qr_scan_text || "");
                    setSmsPhoneText(response.data.sms_phone_text || "");
                    setSmsRegisterButtonText(response.data.sms_register_button_text || "");
                    setSmsConfirmationText(response.data.sms_confirmation_text || "");
                    setSmsNewQueueButtonText(response.data.sms_new_queue_button_text || "");
                }
            } catch (err) {
                console.error("Error fetching settings:", err);
            } finally {
                setSettingsLoading(false);
            }
        };
        fetchSettings();
    }, [branchId]);

    const openPreviewModal = (image, label) => {
        setPreviewModal({ open: true, image, label });
        setIsZoomed(false);
        setZoomOrigin("center center");
    };

    const closePreviewModal = () => {
        setPreviewModal({ open: false, image: null, label: "" });
        setIsZoomed(false);
        setZoomOrigin("center center");
    };

    const handleImageMouseMove = (e) => {
        if (!isZoomed) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setZoomOrigin(`${x}% ${y}%`);
    };

    const handleImageClick = (e) => {
        if (!isZoomed) {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;
            setZoomOrigin(`${x}% ${y}%`);
        }
        setIsZoomed((prev) => !prev);
    };

    const handleSave = async (field, value, setSaving) => {
        try {
            setSaving(true);
            setError(null);
            await updateServiceSettings(branchId, { [field]: value });
            setToast({ open: true, message: "Updated successfully", severity: "success" });
        } catch (err) {
            console.error(`Error saving ${field}:`, err);
            setToast({ open: true, message: `Failed to update ${field}`, severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    // Field config for rendering
    const fields = [
        {
            key: "qr_page_title",
            label: "QR Page Title",
            description: "The large heading shown at the top of the QR scan / check-in page (e.g. your brand or app name).",
            value: qrPageTitle,
            setValue: setQrPageTitle,
            saving: savingQrPageTitle,
            setSaving: setSavingQrPageTitle,
            placeholder: "Queue Ticket",
            preview: previewQrPageTitle,
        },
        {
            key: "service_selection_text",
            label: "Service Selection Text",
            description: "Instruction text shown above the service buttons on the customer selection page.",
            value: serviceSelectionText,
            setValue: setServiceSelectionText,
            saving: savingServiceSelectionText,
            setSaving: setSavingServiceSelectionText,
            placeholder: "We only have one queue, but if we know what you need help with we can better assist you.",
            preview: previewServiceSelectionText,
        },
        {
            key: "next_button_text",
            label: "Next Button Text",
            description: "Label shown on the \"Next\" button on the customer service selection page.",
            value: nextButtonText,
            setValue: setNextButtonText,
            saving: savingNextButtonText,
            setSaving: setSavingNextButtonText,
            placeholder: "Next",
            preview: previewNextButton,
        },
        {
            key: "queue_number_label",
            label: "Queue Number Text",
            description: "Label shown above the customer queue number (e.g., \"Your queue number is\").",
            value: queueNumberLabel,
            setValue: setQueueNumberLabel,
            saving: savingQueueNumberLabel,
            setSaving: setSavingQueueNumberLabel,
            placeholder: "Your queue number is",
            preview: previewQueueNumberLabel,
        },
        {
            key: "selected_services_label",
            label: "Selected Services Label",
            description: "Label shown above the list of selected services on the queue page.",
            value: selectedServicesLabel,
            setValue: setSelectedServicesLabel,
            saving: savingSelectedServicesLabel,
            setSaving: setSavingSelectedServicesLabel,
            placeholder: "Your selected services:",
            preview: previewSelectedServicesLabel,
        },
        {
            key: "waiting_time_message",
            label: "Waiting Time Message",
            description: "Short line shown under the queue number telling the customer how long the wait is. Leave empty to hide it.",
            value: waitingTimeMessage,
            setValue: setWaitingTimeMessage,
            saving: savingWaitingTimeMessage,
            setSaving: setSavingWaitingTimeMessage,
            placeholder: "We will be with you within 10 minutes.",
            preview: previewQueueNumberLabel,
        },
        {
            key: "service_recommendation_text",
            label: "Service Recommendation",
            description: "Optional offer or tip shown below the waiting time on the queue page. Leave empty to hide it.",
            value: serviceRecommendationText,
            setValue: setServiceRecommendationText,
            saving: savingServiceRecommendationText,
            setSaving: setSavingServiceRecommendationText,
            placeholder: "Many customers want help with colour advice - book a 45 min session with us.",
            multiline: true,
            rows: 2,
            preview: previewQueueNumberLabel,
        },
        {
            key: "being_served_message",
            label: "Being Served Message",
            description: "This message is shown when a customer is being served.",
            value: beingServedMessage,
            setValue: setBeingServedMessage,
            saving: savingBeingServedMessage,
            setSaving: setSavingBeingServedMessage,
            placeholder: "You are being served now!",
            preview: previewBeingServedMessage,
        },
        {
            key: "thank_you_message",
            label: "Thank You Message",
            description: "Main heading shown on the thank you page after a visit.",
            value: thankYouMessage,
            setValue: setThankYouMessage,
            saving: savingThankYouMessage,
            setSaving: setSavingThankYouMessage,
            placeholder: "Thank you for your visit!",
            preview: previewThankYouMessage,
        },
        {
            key: "thank_you_description",
            label: "Thank You Description",
            description: "Paragraph shown below the thank you message on the thank you page.",
            value: thankYouDescription,
            setValue: setThankYouDescription,
            saving: savingThankYouDescription,
            setSaving: setSavingThankYouDescription,
            placeholder: "We hope you received good help and are satisfied with our service. Welcome back!",
            preview: previewThankYouDescription,
        },
        {
            key: "thank_you_services_label",
            label: "Thank You Services Label",
            description: "Label shown above the list of services the customer received help with on the thank you page.",
            value: thankYouServicesLabel,
            setValue: setThankYouServicesLabel,
            saving: savingThankYouServicesLabel,
            setSaving: setSavingThankYouServicesLabel,
            placeholder: "Services you received help with:",
            preview: previewThankYouDescription,
        },
        // Registration pages (QR / SMS). Empty = the Norwegian default shown
        // as the placeholder.
        {
            key: "qr_scan_text",
            label: "QR Scan Text",
            description: "Instruction shown with the QR code on the registration pages.",
            value: qrScanText,
            setValue: setQrScanText,
            saving: savingQrScanText,
            setSaving: setSavingQrScanText,
            placeholder: CUSTOMER_FLOW_TEXT_DEFAULTS.qr_scan_text,
        },
        {
            key: "sms_phone_text",
            label: "SMS Phone Number Text",
            description: "Instruction shown above the mobile number field for SMS registration.",
            value: smsPhoneText,
            setValue: setSmsPhoneText,
            saving: savingSmsPhoneText,
            setSaving: setSavingSmsPhoneText,
            placeholder: CUSTOMER_FLOW_TEXT_DEFAULTS.sms_phone_text,
        },
        {
            key: "sms_register_button_text",
            label: "SMS Register Button",
            description: "Button that registers the customer in the queue after choosing services.",
            value: smsRegisterButtonText,
            setValue: setSmsRegisterButtonText,
            saving: savingSmsRegisterButtonText,
            setSaving: setSavingSmsRegisterButtonText,
            placeholder: CUSTOMER_FLOW_TEXT_DEFAULTS.sms_register_button_text,
        },
        {
            key: "sms_confirmation_text",
            label: "SMS Confirmation Text",
            description: "Heading above the queue number on the SMS confirmation page.",
            value: smsConfirmationText,
            setValue: setSmsConfirmationText,
            saving: savingSmsConfirmationText,
            setSaving: setSavingSmsConfirmationText,
            placeholder: CUSTOMER_FLOW_TEXT_DEFAULTS.sms_confirmation_text,
        },
        {
            key: "sms_new_queue_button_text",
            label: "SMS Next Customer Button",
            description: "Button on the confirmation page that returns to the registration page (it also returns by itself after 3 seconds).",
            value: smsNewQueueButtonText,
            setValue: setSmsNewQueueButtonText,
            saving: savingSmsNewQueueButtonText,
            setSaving: setSavingSmsNewQueueButtonText,
            placeholder: CUSTOMER_FLOW_TEXT_DEFAULTS.sms_new_queue_button_text,
        },
    ];

    return (
        <Box>
            <Box sx={{ p: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography variant="h5">
                        Texts & Messages
                    </Typography>
                    <IconButton
                        onClick={() => setRunTutorial(true)}
                        title="Show Tutorial"
                        color="primary"
                        size="small"
                        sx={{ ml: 1 }}
                    >
                        <HelpIcon />
                    </IconButton>
                </Box>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Configure editable text content shown to customers in your queue system.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                <Snackbar
                    open={toast.open}
                    autoHideDuration={3000}
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={() => setToast((t) => ({ ...t, open: false }))}
                        severity={toast.severity}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {toast.message}
                    </Alert>
                </Snackbar>

                {settingsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1, boxShadow: 1 }}>
                    {fields.map((field, index) => (
                        <Box key={field.key} sx={{ mb: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {field.label}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {field.description}
                                    </Typography>
                                </Box>
                                {field.preview && (
                                <IconButton
                                    size="small"
                                    onClick={() => openPreviewModal(field.preview, field.label)}
                                    sx={{
                                        color: "text.secondary",
                                        ml: 1,
                                        flexShrink: 0,
                                        "&:hover": { color: "primary.main" },
                                    }}
                                    title="Show preview"
                                    {...(index === 0 ? { "data-onboarding-id": "onboarding-text-editor-preview" } : {})}
                                >
                                    <ImageIcon />
                                </IconButton>
                                )}
                            </Box>

                            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                                <TextField
                                    fullWidth
                                    multiline={field.multiline || false}
                                    rows={field.rows || 1}
                                    value={field.value}
                                    onChange={(e) => field.setValue(e.target.value)}
                                    placeholder={field.placeholder}
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ maxLength: 255 }}
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={() => handleSave(field.key, field.value, field.setSaving)}
                                    disabled={field.saving}
                                    {...(index === 0 ? { "data-onboarding-id": "onboarding-text-editor-save" } : {})}
                                >
                                    {field.saving ? <CircularProgress size={24} /> : "Save"}
                                </Button>
                            </Box>
                        </Box>
                    ))}
                </Box>
                )}
            </Box>

            <OnboardingTutorialOverlay
                page="text_editor"
                run={runTutorial}
                onPhaseComplete={() => setRunTutorial(false)}
                onClose={() => setRunTutorial(false)}
            />

            {/* Preview Modal with hover-to-zoom */}
            <Dialog
                open={previewModal.open}
                onClose={closePreviewModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
                    {previewModal.label}
                    <IconButton
                        onClick={closePreviewModal}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2, textAlign: "center", backgroundColor: "#f5f5f5" }}>
                    {previewModal.image && (
                        <Box
                            onClick={handleImageClick}
                            onMouseMove={handleImageMouseMove}
                            sx={{
                                overflow: "hidden",
                                borderRadius: 2,
                                cursor: isZoomed ? "zoom-out" : "zoom-in",
                            }}
                        >
                            <img
                                src={previewModal.image}
                                alt={`${previewModal.label} preview`}
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "70vh",
                                    objectFit: "contain",
                                    display: "block",
                                    margin: "0 auto",
                                    transform: isZoomed ? "scale(2.5)" : "scale(1)",
                                    transformOrigin: zoomOrigin,
                                    transition: isZoomed ? "transform 0.2s ease, transform-origin 0s" : "transform 0.3s ease",
                                    userSelect: "none",
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TextEditor;
